import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./PipWindow.module.css";

/*
 * PipWindow
 * Always mounted in DOM. Shown/hidden via CSS only (no unmount).
 * Direct <video>.srcObject — instant, no canvas, no native PiP API.
 *
 * Layout (like Google Meet mini-window):
 *   ┌────────────────────────────┐
 *   │ Header: logo  [Return] [✕] │
 *   ├────────────────────────────┤
 *   │                    ┌──── ┐ │
 *   │  Main peer (big)   │ You │ │
 *   │                    └─────┘ │
 *   ├────────────────────────────┤
 *   │ strip: other peers (scroll)│ ← only when >1 remote
 *   ├────────────────────────────┤
 *   │ 🎙 📷  ·  N participants   │
 *   └────────────────────────────┘
 */

// ── Helper: check if a peer's video is live and enabled ──────────────────────
function peerHasVideo(peer) {
  if (!peer?.stream) return false;
  const tracks = peer.stream.getVideoTracks();
  if (!tracks.length) return false;
  return tracks.some((t) => t.enabled && t.readyState !== "ended");
}

export default function PipWindow({
  visible,
  localStream,
  peers,
  messages = [],
  pinnedId,
  localUserName,
  audioEnabled,
  videoEnabled,
  onPin,
  onUnpin,
  onDismiss,
  onReturnToMeet,
  isDocPip,
  onToggleAudio,
  onToggleVideo,
  onLeave,
}) {
  const pipRef = useRef(null);
  const dragging = useRef(false);
  const dragOff = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: null, y: null });
  const [hovered, setHovered] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);
  const prevMsgCountRef = useRef(messages.length);

  // pinned peer gets main slot; fall back to first remote
  const mainPeer =
    peers.find((p) => p.socketId === pinnedId) || peers[0] || null;
  const stripPeers = peers.filter((p) => p !== mainPeer);
  const pipActive = isDocPip || visible;

  // Reset to default position whenever window opens
  useEffect(() => {
    if (visible) setPos({ x: null, y: null });
  }, [visible]);

  useEffect(() => {
    if (!pipActive) return;
    if (messages.length <= prevMsgCountRef.current) return;
    const latest = messages[messages.length - 1];
    if (!latest) return;
    setToastMsg(latest);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 4000);
  }, [messages, pipActive]);

  useEffect(() => {
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  useEffect(
    () => () => {
      clearTimeout(toastTimerRef.current);
    },
    [],
  );

  // Drag logic
  const onMouseDown = useCallback(
    (e) => {
      if (isDocPip) return;
      if (e.target.closest("button")) return;
      dragging.current = true;
      const r = pipRef.current.getBoundingClientRect();
      dragOff.current = { x: e.clientX - r.left, y: e.clientY - r.top };
      e.preventDefault();
    },
    [isDocPip],
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !pipRef.current) return;
      setPos({
        x: Math.max(
          0,
          Math.min(
            e.clientX - dragOff.current.x,
            window.innerWidth - pipRef.current.offsetWidth,
          ),
        ),
        y: Math.max(
          0,
          Math.min(
            e.clientY - dragOff.current.y,
            window.innerHeight - pipRef.current.offsetHeight,
          ),
        ),
      });
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const posStyle = isDocPip
    ? {
        width: "100vw",
        height: "100vh",
        left: 0,
        top: 0,
        right: "auto",
        bottom: "auto",
        border: "none",
        borderRadius: 0,
        boxShadow: "none",
      }
    : pos.x !== null
      ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
      : {};

  return (
    <div
      ref={pipRef}
      className={`${styles.pip} ${visible ? styles.pipVisible : styles.pipHidden}`}
      style={posStyle}
      onMouseDown={onMouseDown}
    >
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.logo}>
          <img src="/logo.png" alt="QuantumMeet" className={styles.logoImage} />{" "}
          QuantumMeet
        </span>
        <div className={styles.headerBtns}>
          <button className={styles.returnBtn} onClick={onReturnToMeet}>
            ↩ Return
          </button>
          <button className={styles.closeBtn} onClick={onDismiss}>
            ✕
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className={styles.mainArea}>
        {toastMsg && (
          <div className={styles.chatToast}>
            <span className={styles.chatToastUser}>{toastMsg.userName || "User"}</span>
            <span className={styles.chatToastText}>{toastMsg.message}</span>
          </div>
        )}

        {/* Big remote tile */}
        {mainPeer ? (
          <Tile
            stream={mainPeer.stream}
            userName={mainPeer.userName}
            muted={false}
            mirrored={false}
            videoEnabled={peerHasVideo(mainPeer)}
            isPinned={pinnedId === mainPeer.socketId}
            hovered={hovered === mainPeer.socketId}
            onEnter={() => setHovered(mainPeer.socketId)}
            onLeave={() => setHovered(null)}
            onPin={() =>
              pinnedId === mainPeer.socketId
                ? onUnpin?.()
                : onPin?.(mainPeer.socketId)
            }
            pinLabel={pinnedId === mainPeer.socketId ? "Unpin" : "Pin"}
            big
          />
        ) : (
          <div className={styles.emptyMain}>
            <span>👥</span>
            <p>Waiting for others…</p>
          </div>
        )}

        {/* Local self-view — corner */}
        <div
          className={`${styles.selfCorner} ${pinnedId === "local" ? styles.selfPinned : ""}`}
          onMouseEnter={() => setHovered("local")}
          onMouseLeave={() => setHovered(null)}
        >
          <VideoEl
            stream={localStream}
            muted
            mirrored
            videoEnabled={videoEnabled}
          />
          {!videoEnabled && (
            <div className={styles.avatar}>
              {localUserName?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className={styles.selfLabel}>
            {!audioEnabled && "🔇 "}
            {localUserName}
          </div>
          {hovered === "local" && (
            <div className={styles.hoverOverlay}>
              <button
                className={styles.overlayBtn}
                onClick={() =>
                  pinnedId === "local" ? onUnpin?.() : onPin?.("local")
                }
              >
                📌 {pinnedId === "local" ? "Unpin" : "Pin"}
              </button>
              <button
                className={`${styles.overlayBtn} ${styles.overlayBtnRed}`}
                onClick={onDismiss}
              >
                ✕ Hide
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Strip — other remote peers */}
      {stripPeers.length > 0 && (
        <div className={styles.strip}>
          {stripPeers.map((p) => (
            <Tile
              key={p.socketId}
              stream={p.stream}
              userName={p.userName}
              muted={false}
              mirrored={false}
              videoEnabled={peerHasVideo(p)}
              isPinned={pinnedId === p.socketId}
              hovered={hovered === p.socketId}
              onEnter={() => setHovered(p.socketId)}
              onLeave={() => setHovered(null)}
              onPin={() =>
                pinnedId === p.socketId ? onUnpin?.() : onPin?.(p.socketId)
              }
              pinLabel={pinnedId === p.socketId ? "Unpin" : "Pin"}
            />
          ))}
        </div>
      )}

      {/* Status bar */}
      <div className={styles.statusBar}>
        <div className={styles.mediaBtns}>
          {onToggleAudio && (
            <button
              className={`${styles.mediaBtn} ${!audioEnabled ? styles.mediaBtnOff : ""}`}
              onClick={onToggleAudio}
              title={audioEnabled ? "Mute" : "Unmute"}
            >
              {audioEnabled ? "🎙️" : "🔇"}
            </button>
          )}
          {onToggleVideo && (
            <button
              className={`${styles.mediaBtn} ${!videoEnabled ? styles.mediaBtnOff : ""}`}
              onClick={onToggleVideo}
              title={videoEnabled ? "Stop camera" : "Start camera"}
            >
              {videoEnabled ? "📷" : "📵"}
            </button>
          )}
          {onLeave && (
            <button
              className={`${styles.mediaBtn} ${styles.mediaBtnDanger}`}
              onClick={onLeave}
              title="Leave call"
            >
              📞
            </button>
          )}
          <button
            className={`${styles.mediaBtn} ${chatOpen ? styles.mediaBtnOn : ""}`}
            onClick={() => setChatOpen((v) => !v)}
            title={chatOpen ? "Hide chat" : "Show chat"}
          >
            💬
          </button>
        </div>
        {!isDocPip && (
          <span className={styles.count}>{peers.length + 1} participants</span>
        )}
      </div>

      {chatOpen && (
        <div className={styles.chatPanel}>
          {messages.length === 0 ? (
            <p className={styles.chatEmpty}>No chat messages yet.</p>
          ) : (
            <div className={styles.chatList}>
              {messages.slice(-50).map((m, i) => (
                <div key={`${m.timestamp || i}-${i}`} className={styles.chatLine}>
                  <span className={styles.chatUser}>{m.userName || "User"}:</span>{" "}
                  <span className={styles.chatText}>{m.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Tile ────────────────────────────────────────────────────────────────── */
function Tile({
  stream,
  userName,
  muted,
  mirrored,
  videoEnabled,
  isPinned,
  hovered,
  onEnter,
  onLeave,
  onPin,
  pinLabel,
  big,
}) {
  // Show avatar whenever there's no live video track
  const showAvatar =
    !videoEnabled || !stream || stream.getVideoTracks().length === 0;

  return (
    <div
      className={`${styles.tile} ${isPinned ? styles.tilePinned : ""} ${big ? styles.tileBig : styles.tileSmall}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <VideoEl
        stream={stream}
        muted={muted}
        mirrored={mirrored}
        videoEnabled={videoEnabled}
      />

      {/* Avatar overlay when video is off or stream is missing */}
      {showAvatar && (
        <div className={styles.tileAvatar}>
          <span>{userName?.[0]?.toUpperCase() || "?"}</span>
        </div>
      )}

      <div className={styles.tileLabel}>{userName}</div>
      {isPinned && <div className={styles.pinBadge}>📌</div>}
      {hovered && (
        <div className={styles.hoverOverlay}>
          <button className={styles.overlayBtn} onClick={onPin}>
            📌 {pinLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Video element — direct srcObject wiring ─────────────────────────────── */
function VideoEl({ stream, muted, mirrored, videoEnabled }) {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.srcObject = stream || null;
    if (stream) {
      const tryPlay = () => video.play().catch(() => {});
      video.addEventListener("loadedmetadata", tryPlay, { once: true });
      if (video.readyState >= 1) tryPlay();

      const onTrackAdded = () => {
        video.srcObject = null;
        video.srcObject = stream;
        tryPlay();
      };
      stream.addEventListener("addtrack", onTrackAdded);

      return () => {
        video.removeEventListener("loadedmetadata", tryPlay);
        stream.removeEventListener("addtrack", onTrackAdded);
      };
    }
  }, [stream]);

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={styles.video}
      style={{
        ...(mirrored ? { transform: "scaleX(-1)" } : undefined),
        // Keep in DOM but hidden so playback resumes instantly when video turns on
        opacity: videoEnabled === false ? 0 : 1,
        transition: "opacity 0.2s ease",
      }}
    />
  );
}
